<!--
  ═══════════════════════════════════════════════════════════════════════════
  WRAPPER - ATRIBUTOS DINÁMICOS PARA RECURSO
  ═══════════════════════════════════════════════════════════════════════════
  
  Wrapper específico para renderizar atributos dinámicos de Recurso.
  Maneja criterios de visibilidad por Módulo.
  
  Uso:
  ```vue
  <RecursoAtributosForm
    v-model="recurso.AtributosJSON"
    :modulo="2"
    @change="handleAtributosChange"
  />
  ```
  
  @author Sistema SISPRO ERP
  @version 1.0.0
-->

<template>
  <div v-if="shouldShowComponent">
    <el-divider content-position="left">
      <span style="font-weight: 600; font-size: 16px">Atributos Personalizados</span>
    </el-divider>
    <AtributosDinamicosForm
      ref="formRef"
      :definiciones="definiciones"
      :model-value="modelValue"
      :readonly="readonly"
      :layout="layout"
      :columns="columns"
      :show-validation-errors="showValidationErrors"
      @update:modelValue="emit('update:modelValue', $event)"
      @validation-error="emit('validation-error', $event)"
      @change="emit('change', $event)"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import AtributosDinamicosForm from './AtributosDinamicosForm.vue';
import atributosDinamicosService from '@/services/atributos_dinamicos_service';

// ══════════════════════════════════════════════════════════════════════════
// PROPS & EMITS
// ══════════════════════════════════════════════════════════════════════════

const props = defineProps({
  modelValue: {
    type: [Object, String],
    default: () => ({}),
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String,
    default: 'grid',
  },
  columns: {
    type: Number,
    default: 2,
  },
  showValidationErrors: {
    type: Boolean,
    default: false,
  },
  // Criterios de visibilidad específicos de Recurso
  modulo: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'validation-error', 'change']);

// ══════════════════════════════════════════════════════════════════════════
// ESTADO
// ══════════════════════════════════════════════════════════════════════════

const formRef = ref(null);
const definiciones = ref([]);
const loading = ref(false);

// ══════════════════════════════════════════════════════════════════════════
// COMPUTED
// ══════════════════════════════════════════════════════════════════════════

/**
 * Determina si se debe mostrar el componente
 * - En contexto ABM (sin modulo): siempre mostrar
 * - En contexto módulo: solo mostrar si hay definiciones
 */
const shouldShowComponent = computed(() => {
  // En contexto ABM (sin módulo especificado), siempre mostrar
  // if (!props.modulo) {
  //   return true;
  // }

  // En contexto módulo, solo mostrar si hay definiciones
  return definiciones.value.length > 0;
});

// ══════════════════════════════════════════════════════════════════════════
// MÉTODOS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Carga definiciones de atributos para Recurso
 */
async function loadDefiniciones() {
  loading.value = true;
  try {
    // Usar el nuevo endpoint que filtra según criterios
    const opciones = {
      contexto: props.modulo ? 'modulo' : 'abm',
    };

    // Agregar módulo si está especificado
    if (props.modulo) {
      opciones.modulo = props.modulo;
    }

    const resultado = await atributosDinamicosService.getDefinicionesVisiblesConEditable(
      'Recurso',
      opciones,
    );

    definiciones.value = resultado;
  } catch (error) {
    console.error('Error cargando atributos de Recurso:', error);
    ElMessage.error('Error cargando configuración de atributos');
  } finally {
    loading.value = false;
  }
}

/**
 * Valida el formulario
 */
async function validate() {
  return await formRef.value?.validate();
}

/**
 * Resetea el formulario
 */
function reset() {
  formRef.value?.reset();
}

// ══════════════════════════════════════════════════════════════════════════
// LIFECYCLE
// ══════════════════════════════════════════════════════════════════════════

onMounted(() => {
  loadDefiniciones();
});

// Recargar cuando cambien criterios de visibilidad
watch(
  () => props.modulo,
  () => {
    loadDefiniciones();
  },
);

// ══════════════════════════════════════════════════════════════════════════
// EXPOSE
// ══════════════════════════════════════════════════════════════════════════

defineExpose({
  validate,
  reset,
  loadDefiniciones,
  // Propiedades computadas delegadas directamente desde el componente hijo
  get isValid() {
    return formRef.value?.isValid ?? true;
  },
  get atributos() {
    return formRef.value?.atributos ?? {};
  },
  get shouldShow() {
    return shouldShowComponent.value;
  },
});
</script>
